import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, LifeBuoy } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "../../api/client";
import { Callout } from "../../components/Callout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Motion, Presence } from "../../components/presence";
import { Select } from "../../components/Select";
import { Badge, Button, Field, inputClass, SectionLabel } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { actionLabel, authDateTime, authTime } from "../authText";
import { adminErrorText, csvCell, lockLabel, quotaLabel } from "./adminText";
import type { AuditFilters, DefaultShare, UnitAccount, UnitAuditRow } from "./adminTypes";
import { ReauthCancelled, withReauth } from "./reauth";
import { Waiting } from "../../components/Waiting";

/** Onglets Sessions, Journal et Réglages de « Comptes de l'unité » (docs/17 §5.7, §5.8, §8.3). */

type Message = (message: { tone: "ok" | "danger"; text: string }) => void;

function reported(onMessage: Message) {
  return (error: unknown) => {
    if (!(error instanceof ReauthCancelled)) onMessage({ tone: "danger", text: adminErrorText(error) });
  };
}

const HEAD = "border-b border-line pb-1.5 text-meta font-medium text-ink-muted";
const ROW = "min-h-8 border-b border-line py-1.5 last:border-b-0";

// --- Sessions ---------------------------------------------------------------------------------------------------

export function SessionsTab({ onMessage }: { onMessage: Message }) {
  const queryClient = useQueryClient();
  const view = useQuery({ queryKey: ["unit-sessions"], queryFn: api.unit.sessions, staleTime: 0, refetchInterval: 60_000 });
  const [revoking, setRevoking] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["unit-sessions"] });
  const end = useMutation({ mutationFn: (ref: string) => withReauth(() => api.unit.endSession(ref)), onSuccess: refresh, onError: reported(onMessage) });
  const clearSeat = useMutation({
    mutationFn: (ref: string) => withReauth(() => api.unit.clearSeat(ref)),
    onSuccess: refresh,
    onError: reported(onMessage),
  });
  const clearReplace = useMutation({ mutationFn: () => withReauth(() => api.unit.clearReplace()), onSuccess: refresh, onError: reported(onMessage) });
  const revokeAll = useMutation({
    mutationFn: () => withReauth(() => api.unit.revokeAll()),
    onSuccess: () => {
      setRevoking(false);
      // La session de l'administrateur est fermée aussi : la prochaine requête pose la surcouche « Session fermée ».
      void queryClient.invalidateQueries();
    },
    onError: (error) => {
      setRevoking(false);
      reported(onMessage)(error);
    },
  });
  if (view.error) return <Callout tone="danger">{adminErrorText(view.error)}</Callout>;
  if (!view.data) return <p className="text-ui text-ink-muted">{<Waiting>{t("Chargement…")}</Waiting>}</p>;
  const { sessions, delayed_seats: delayed, replace_delay: replace } = view.data;
  return (
    <Motion origin="fade" className="space-y-4">
      <section>
        <div className="flex items-center justify-between gap-3">
          <SectionLabel>{t("Sessions ouvertes")}</SectionLabel>
          <Button size="sm" variant="danger-ghost" onClick={() => setRevoking(true)}>
            {t("Déconnecter tout le monde")}
          </Button>
        </div>
        <div className={`mt-1 hidden grid-cols-[1.2fr_1fr_auto_auto_auto] gap-x-3 sm:grid ${HEAD}`}>
          <span>{t("Compte")}</span>
          <span>{t("Poste")}</span>
          <span>{t("Ouverte le")}</span>
          <span>{t("Dernière activité")}</span>
          <span className="w-16" />
        </div>
        <ul>
          {sessions.map((row) => (
            <li key={row.ref} className={`grid grid-cols-[1fr_auto] items-center gap-x-3 sm:grid-cols-[1.2fr_1fr_auto_auto_auto] ${ROW}`}>
              <span className="min-w-0 truncate text-ui text-ink">
                {row.account.display_name}
                {row.current && <span className="ml-1.5 text-ink-muted">{t("(cette session)")}</span>}
                {row.locked && (
                  <Badge size="sm" className="ml-1.5">
                    {t("Verrouillée")}
                  </Badge>
                )}
              </span>
              <span className="hidden min-w-0 truncate text-meta text-ink-muted sm:block">{row.seat_name || "—"}</span>
              <span className="hidden text-meta text-ink-muted tabular-nums sm:block">{authDateTime(row.created_at)}</span>
              <span className="hidden text-meta text-ink-muted tabular-nums sm:block">{authTime(row.last_active_at)}</span>
              <span className="flex w-16 justify-end">
                {!row.current && (
                  <Button size="sm" variant="ghost" disabled={end.isPending} onClick={() => end.mutate(row.ref)}>
                    {t("Fermer")}
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ul>
      </section>
      <Presence show={replace !== null}>
        {replace && (
          <Callout
            tone="warn"
            action={
              <Button size="sm" variant="secondary" disabled={clearReplace.isPending} onClick={() => clearReplace.mutate()}>
                {t("Lever le délai de reprise de place")}
              </Button>
            }
          >
            {t("Reprise de place retardée jusqu'à {heure} après {n} identifiants d'administrateur refusés", {
              heure: authTime(replace.until),
              n: replace.failures,
            })}
          </Callout>
        )}
      </Presence>
      <section>
        <SectionLabel>{t("Postes retardés")}</SectionLabel>
        {delayed.length === 0 ? (
          <p className="mt-1 text-meta text-ink-muted">{t("Aucun poste retardé.")}</p>
        ) : (
          <ul className="mt-1">
            {delayed.map((seat) => (
              <li key={seat.ref} className={`grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 ${ROW}`}>
                <span className="min-w-0 truncate text-ui text-ink">{seat.name || t("Poste sans nom")}</span>
                <span className="text-meta text-ink-muted tabular-nums">{t("{n} échecs", { n: seat.failures })}</span>
                <span className="text-meta text-ink-muted tabular-nums">{t("jusqu'à {heure}", { heure: authTime(seat.until) })}</span>
                <Button size="sm" variant="secondary" disabled={clearSeat.isPending} onClick={() => clearSeat.mutate(seat.ref)}>
                  {t("Lever le blocage de ce poste")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <Presence show={revoking}>
        {revoking && (
          <ConfirmDialog
            title={t("Déconnecter tout le monde")}
            confirmLabel={t("Déconnecter tout le monde")}
            busyLabel={t("Déconnexion…")}
            busy={revokeAll.isPending}
            onConfirm={() => revokeAll.mutate()}
            onCancel={() => setRevoking(false)}
          >
            {t("Toutes les sessions de l'unité seront fermées, la vôtre comprise.")}
          </ConfirmDialog>
        )}
      </Presence>
    </Motion>
  );
}

// --- Journal ----------------------------------------------------------------------------------------------------

/** Marque « À examiner » d'un événement, en clair. */
function reviewMark(row: UnitAuditRow): string | null {
  if (!row.review) return null;
  if (row.action === "account.code_used") return t("Code utilisé depuis le poste de son émetteur");
  if (row.action === "licence.replace_failed") return t("Identifiants d'administrateur refusés à l'activation");
  if (row.details.review_reason === "approver_linked") return t("Approbateur créé ou nommé par le demandeur");
  if (row.details.review_reason === "target_code") return t("Dernier code du compte nommé émis par le demandeur");
  if (row.details.review_reason === "approver_recovered") return t("Approbateur repris par le code de secours depuis moins de 30 jours");
  return t("Approbation depuis le poste du demandeur");
}

const ACTION_FILTERS = [
  "auth.login",
  "auth.login_failed",
  "account.created",
  "account.code_issued",
  "account.code_used",
  "account.locked",
  "account.disabled",
  "item.taken_over",
  "exercise.owner",
  "admin.named",
  "request.filed",
  "request.approved",
  "recovery.used",
  "licence.released",
  "licence.replaced",
  "licence.replace_failed",
  "unit.settings",
  "ops.",
];

/** Fichier CSV du journal affiché (séparateur « ; », lisible par un tableur), libellés dans la langue choisie. */
function downloadCsv(rows: UnitAuditRow[]): void {
  const head = [t("Date"), t("Compte"), t("Poste"), t("Adresse"), t("Action"), t("Cible"), t("À examiner")];
  const lines = rows.map((row) =>
    [authDateTime(row.at), row.actor ?? "", row.seat ?? "", row.ip ?? "", actionLabel(row.action), row.target ?? "", reviewMark(row) ?? ""]
      .map(csvCell)
      .join(";"),
  );
  const blob = new Blob(["﻿" + [head.join(";"), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "journal-unite.csv";
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function AuditList({ rows }: { rows: UnitAuditRow[] }) {
  return (
    <ul>
      {rows.map((row) => {
        const mark = reviewMark(row);
        return (
          <li key={row.id} className={`grid grid-cols-[auto_1fr] gap-x-3 ${ROW}`}>
            <span className="pt-px text-meta text-ink-muted tabular-nums">{authDateTime(row.at)}</span>
            <span className="min-w-0 text-ui leading-snug text-ink">
              <span className="font-medium">{actionLabel(row.action)}</span>
              {row.target && <span className="text-ink-soft"> · {row.target}</span>}
              <span className="block text-meta text-ink-muted">
                {[row.actor, row.seat, row.ip].filter(Boolean).join(" · ")}
              </span>
              {mark && (
                <Badge tone="warn" dot className="mt-1">
                  {mark}
                </Badge>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function AuditTab({ accounts }: { accounts: UnitAccount[] }) {
  const [filters, setFilters] = useState<AuditFilters>({});
  const audit = useQuery({ queryKey: ["unit-audit", filters], queryFn: () => api.unit.audit(filters), staleTime: 0 });
  const set = (key: keyof AuditFilters, value: string) => setFilters((f) => ({ ...f, [key]: value || undefined }));
  return (
    <Motion origin="fade" className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label={t("Compte")}>
          <Select value={filters.account ?? ""} onChange={(e) => set("account", e.target.value)} aria-label={t("Compte")}>
            <option value="">{t("Tous")}</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.display_name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("Du")}>
          <input type="date" className={inputClass} value={filters.from ?? ""} onChange={(e) => set("from", e.target.value)} />
        </Field>
        <Field label={t("Au")}>
          <input type="date" className={inputClass} value={filters.to ?? ""} onChange={(e) => set("to", e.target.value)} />
        </Field>
        <Field label={t("Action")}>
          <Select value={filters.action ?? ""} onChange={(e) => set("action", e.target.value)} aria-label={t("Action")}>
            <option value="">{t("Toutes")}</option>
            {ACTION_FILTERS.map((action) => (
              <option key={action} value={action}>
                {actionLabel(action)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {audit.error ? (
        <Callout tone="danger">{adminErrorText(audit.error)}</Callout>
      ) : !audit.data ? (
        <p className="text-ui text-ink-muted">{<Waiting>{t("Chargement…")}</Waiting>}</p>
      ) : (
        <>
          <Presence show={audit.data.review.length > 0}>
            {audit.data.review.length > 0 && (
              <Motion origin="top" className="rounded-lg bg-warn-soft px-3 py-2">
                <h3 className="text-ui font-semibold text-ink">{t("À examiner")}</h3>
                <AuditList rows={audit.data.review} />
              </Motion>
            )}
          </Presence>
          <div className="flex items-center justify-between gap-3">
            <SectionLabel>{t("Journal de l'unité")}</SectionLabel>
            <Button size="sm" variant="secondary" onClick={() => downloadCsv(audit.data.events)} disabled={!audit.data.events.length}>
              <Download size={14} /> {t("Télécharger (CSV)")}
            </Button>
          </div>
          {audit.data.events.length ? (
            <AuditList rows={audit.data.events} />
          ) : (
            <p className="text-meta text-ink-muted">{t("Aucun événement pour ces filtres.")}</p>
          )}
          <p className="text-meta leading-snug text-ink-muted">
            {t("Le journal garde les événements {n} jours ; il ne contient jamais de mot de passe, de code ni de contenu de dossier.", {
              n: audit.data.retention_days,
            })}
          </p>
        </>
      )}
    </Motion>
  );
}

// --- Réglages ---------------------------------------------------------------------------------------------------

export function SettingsTab({ onMessage, onRecovery }: { onMessage: Message; onRecovery: (code: string) => void }) {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ["unit-settings"], queryFn: api.unit.settings, staleTime: 0 });
  const [name, setName] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  useEffect(() => {
    if (settings.data && name === null) setName(settings.data.unit_name ?? "");
  }, [settings.data, name]);
  const save = useMutation({
    mutationFn: (body: Parameters<typeof api.unit.saveSettings>[0]) => withReauth(() => api.unit.saveSettings(body)),
    onSuccess: (next) => {
      queryClient.setQueryData(["unit-settings"], next);
      void queryClient.invalidateQueries({ queryKey: ["auth-session"] });
      onMessage({ tone: "ok", text: t("Réglage enregistré") });
    },
    onError: reported(onMessage),
  });
  const regenerate = useMutation({
    mutationFn: () => withReauth(() => api.unit.regenerateRecovery()),
    onSuccess: (result) => {
      setRegenerating(false);
      onRecovery(result.code);
      void queryClient.invalidateQueries({ queryKey: ["unit-settings"] });
    },
    onError: (error) => {
      setRegenerating(false);
      reported(onMessage)(error);
    },
  });
  if (settings.error) return <Callout tone="danger">{adminErrorText(settings.error)}</Callout>;
  const data = settings.data;
  if (!data) return <p className="text-ui text-ink-muted">{<Waiting>{t("Chargement…")}</Waiting>}</p>;
  const shareLabels: Record<DefaultShare, string> = {
    none: t("Privé"),
    reader: t("Toute l'unité, lecture seule"),
    editor: t("Toute l'unité, modification"),
  };
  const typed = (name ?? "").trim();
  const nameChanged = name !== null && typed !== (data.unit_name ?? "");
  return (
    <Motion origin="fade" className="max-w-[520px] space-y-4">
      <div className="flex items-end gap-2">
        <Field label={t("Nom de l'unité")} hint={t("Ex. : 2e RIMa · PC GTIA")} className="min-w-0 flex-1">
          <input className={inputClass} value={name ?? ""} maxLength={80} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Button variant="secondary" className="mb-5" disabled={!nameChanged || save.isPending} onClick={() => save.mutate({ unit_name: typed || null })}>
          {t("Enregistrer")}
        </Button>
      </div>
      <Field label={t("Verrouillage après inactivité")} hint={t("2 ou 4 h pour un écran de situation DIREX laissé affiché.")}>
        <Select value={String(data.lock_minutes)} onChange={(e) => save.mutate({ lock_minutes: Number(e.target.value) })} aria-label={t("Verrouillage après inactivité")}>
          {data.choices.lock_minutes.map((minutes) => (
            <option key={minutes} value={String(minutes)}>
              {lockLabel(minutes)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t("Partage des nouveaux dossiers")}>
        <Select value={data.default_share} onChange={(e) => save.mutate({ default_share: e.target.value as DefaultShare })} aria-label={t("Partage des nouveaux dossiers")}>
          {data.choices.default_share.map((share) => (
            <option key={share} value={share}>
              {shareLabels[share]}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t("Plafond de stockage par compte")}>
        <Select
          value={data.account_quota_bytes === null ? "" : String(data.account_quota_bytes)}
          onChange={(e) => save.mutate({ account_quota_bytes: e.target.value ? Number(e.target.value) : null })}
          aria-label={t("Plafond de stockage par compte")}
        >
          {data.choices.account_quota_bytes.map((bytes) => (
            <option key={bytes ?? "none"} value={bytes === null ? "" : String(bytes)}>
              {quotaLabel(bytes)}
            </option>
          ))}
        </Select>
      </Field>
      <div className="border-t border-line pt-4">
        <SectionLabel>{t("Code de secours de l'unité")}</SectionLabel>
        <p className="mt-1 text-meta leading-snug text-ink-muted">
          {data.recovery_issued_at
            ? t("Code en service depuis le {date}. Régénérez-le devant le commandement et gardez-le sous pli.", { date: authDateTime(data.recovery_issued_at) })
            : t("Aucun code de secours en service : régénérez-en un et gardez-le sous pli.")}
        </p>
        <Button variant="secondary" className="mt-2" onClick={() => setRegenerating(true)}>
          <LifeBuoy size={14} /> {t("Régénérer le code de secours…")}
        </Button>
      </div>
      <Presence show={regenerating}>
        {regenerating && (
          <ConfirmDialog
            title={t("Régénérer le code de secours")}
            confirmLabel={t("Régénérer")}
            busyLabel={t("Régénération…")}
            busy={regenerate.isPending}
            onConfirm={() => regenerate.mutate()}
            onCancel={() => setRegenerating(false)}
          >
            {t("L'ancien code ne servira plus. Tous les administrateurs de l'unité en seront prévenus.")}
          </ConfirmDialog>
        )}
      </Presence>
    </Motion>
  );
}
